/**
 * The one thing between tapping a tradable card and asking to meet someone.
 *
 * It names the person, says what happens next, and offers their trade binder. It does
 * not say why you would want to meet them, because that is the part people work out for
 * themselves: you look through a binder, you want what is in it, you ask. Spelling it
 * out would turn a discovery into a rule.
 *
 * The card that got you here is not mentioned at all. It was a door.
 */
import { X } from 'lucide-react';
import haptics from '../../utils/haptics';
import { useTheme } from '../../context/ThemeContext';
import {
  confirmTitle, CONFIRM_BODY, CONFIRM_LOOK, CONFIRM_GO,
} from '../../services/tradeRequest';

export default function StartChatSheet({
  isOpen,
  username,
  avatarUrl,
  binderName,
  binderCount,
  sending = false,
  onLookAtBinder,
  onStart,
  onClose,
}) {
  const { theme } = useTheme();
  if (!isOpen) return null;

  return (
    <div style={S.scrim} onClick={onClose} role="presentation">
      <div
        style={{ ...S.box, background: theme.bgSheet || '#101114', borderColor: theme.border }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={confirmTitle(username)}
      >
        <button
          type="button"
          style={{ ...S.close, borderColor: theme.border, color: theme.textSecondary }}
          onClick={onClose}
          aria-label="Close"
        >
          <X size={16} />
        </button>

        <div style={S.head}>
          {avatarUrl
            ? <img src={avatarUrl} alt="" style={S.avatar} />
            : <div style={{ ...S.avatar, ...S.avatarBlank, borderColor: theme.border }} />}
          <div style={{ minWidth: 0 }}>
            <h2 style={{ ...S.title, color: theme.text }}>{confirmTitle(username)}</h2>
            <p style={{ ...S.body, color: theme.textSecondary }}>{CONFIRM_BODY}</p>
          </div>
        </div>

        {/* Offered, not explained. */}
        {onLookAtBinder && (
          <button
            type="button"
            style={{ ...S.look, borderColor: theme.border, color: theme.text }}
            onClick={() => { haptics.tap(); onLookAtBinder(); }}
          >
            <span>{CONFIRM_LOOK}</span>
            {binderCount != null && (
              <span style={{ ...S.lookMeta, color: theme.textMuted }}>
                {binderName || 'Trade Binder'} &middot; {binderCount} cards
              </span>
            )}
          </button>
        )}

        <button
          type="button"
          style={{ ...S.cta, ...(sending ? S.ctaOff : {}) }}
          disabled={sending}
          onClick={() => { haptics.tap(); onStart(); }}
        >
          {sending ? 'Sending' : CONFIRM_GO}
        </button>
      </div>
    </div>
  );
}

const S = {
  scrim: {
    position: 'fixed', inset: 0, zIndex: 100040, background: 'rgba(0,0,0,.78)',
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0,
  },
  box: {
    position: 'relative', width: '100%', maxWidth: 480,
    border: '0.5px solid #24262b', borderRadius: '18px 18px 0 0',
    padding: '18px 16px calc(20px + var(--safe-area-bottom, 0px))', boxSizing: 'border-box',
  },
  close: {
    position: 'absolute', top: 12, right: 12,
    width: 30, height: 30, borderRadius: 15, border: '1px solid #2b323d',
    background: 'transparent', display: 'flex', alignItems: 'center',
    justifyContent: 'center', cursor: 'pointer',
  },
  head: { display: 'flex', gap: 12, alignItems: 'center', paddingRight: 34, marginBottom: 16 },
  avatar: { width: 46, height: 46, borderRadius: 23, objectFit: 'cover', flex: 'none' },
  avatarBlank: { background: 'linear-gradient(140deg,#3c4756,#242a34)', border: '1px solid #2b323d' },
  title: { fontSize: 17, fontWeight: 700, margin: 0, lineHeight: 1.25 },
  body: { fontSize: 13, margin: '3px 0 0', lineHeight: 1.45 },
  look: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
    width: '100%', textAlign: 'left', padding: '12px 14px', marginBottom: 9,
    borderRadius: 11, border: '1px solid #2b323d', background: 'transparent',
    fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
  lookMeta: { fontSize: 11.5, fontWeight: 500 },
  cta: {
    width: '100%', border: 0, background: '#4ade80', color: '#06210f',
    fontWeight: 700, borderRadius: 11, padding: '14px 16px',
    cursor: 'pointer', fontSize: 14.5, fontFamily: 'inherit',
  },
  ctaOff: { opacity: 0.5, cursor: 'default' },
};
